import { ArrowRight, CalendarDays, CreditCard, FileSignature, FileText, LayoutDashboard, MailCheck } from "lucide-react";
import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { FormPortalHeader } from "./FormPortalHeader";

export type AdminWorkflowKey =
  | "proposal-dashboard"
  | "create-proposal"
  | "agreement-records"
  | "payment-records"
  | "payment-settings"
  | "scheduler-settings"
  | "welcome-email-settings"
  | "onboarding-settings";

const workflowContent = {
  "proposal-dashboard": {
    icon: LayoutDashboard,
    title: "Proposal Dashboard",
    eyebrow: "Forms Portal Admin Workflow",
    description:
      "Review proposal, agreement, payment, scheduler, and onboarding workflow areas from the private Forms Portal admin hub.",
    status: "Connected",
    focus: [
      { label: "Proposal status overview", to: "/admin/proposal-dashboard", routeLabel: "Open proposal dashboard" },
      { label: "Agreement and payment follow-up", to: "/admin/agreement-records#agreement-status", routeLabel: "Open agreement records" },
      { label: "Scheduler and welcome email readiness", to: "/admin/welcome-email-settings#welcome-email-settings", routeLabel: "Open welcome settings" },
    ],
  },
  "create-proposal": {
    icon: FileText,
    title: "Create Proposal",
    eyebrow: "Forms Portal Admin Workflow",
    description:
      "Create and manage Kreatethedate proposals, selected services, package pricing, add-ons, and proposal links.",
    status: "Connected",
    focus: [
      { label: "Client proposal details", to: "/admin/proposals#proposal-editor", routeLabel: "Open proposal editor" },
      { label: "Selected services and add-ons", to: "/admin/proposals#proposal-editor", routeLabel: "Open proposal editor" },
      { label: "Proposal link and next-step tracking", to: "/admin/leads", routeLabel: "Open lead inbox" },
    ],
  },
  "agreement-records": {
    icon: FileSignature,
    title: "Agreement Records",
    eyebrow: "Forms Portal Admin Workflow",
    description:
      "Open the dedicated agreement records workspace to review agreement status, signature details, and linked proposal history.",
    status: "Connected",
    focus: [
      { label: "Agreement status", to: "/admin/agreement-records#agreement-status", routeLabel: "Open agreement records" },
      { label: "Signature details", to: "/admin/agreement-records#signature-details", routeLabel: "Open agreement records" },
      { label: "Linked proposal and service records", to: "/admin/proposals#proposal-records", routeLabel: "Open proposal workspace" },
    ],
  },
  "payment-records": {
    icon: CreditCard,
    title: "Payment Records",
    eyebrow: "Forms Portal Admin Workflow",
    description:
      "Open the dedicated payment records workspace to review invoices, balances, deposits, and payment history.",
    status: "Connected",
    focus: [
      { label: "Payment status", to: "/admin/payment-records#payment-status", routeLabel: "Open payment records" },
      { label: "Payment link readiness", to: "/admin/payment-settings#payment-link-readiness", routeLabel: "Open payment settings" },
      { label: "Internal payment follow-up", to: "/admin/executive-dashboard#follow-up-center", routeLabel: "Open executive dashboard" },
    ],
  },
  "payment-settings": {
    icon: CreditCard,
    title: "Payment Settings",
    eyebrow: "Forms Portal Admin Workflow",
    description: "Configure Stripe tax rules, payment plan defaults, deposit behavior, and invoice preferences.",
    status: "Connected",
    focus: [
      { label: "Tax settings", to: "/admin/payment-settings#tax-settings", routeLabel: "Open payment settings" },
      { label: "Deposit and payment plan defaults", to: "/admin/payment-settings#payment-plan-defaults", routeLabel: "Open payment settings" },
      { label: "Invoice behavior", to: "/admin/payment-settings#invoice-behavior", routeLabel: "Open payment settings" },
    ],
  },
  "scheduler-settings": {
    icon: CalendarDays,
    title: "Scheduler Settings",
    eyebrow: "Forms Portal Admin Workflow",
    description: "Configure scheduling links, kickoff call status, scheduler requirements, and admin scheduling follow-up.",
    status: "Scheduler settings page",
    focus: [
      { label: "Scheduler link settings", to: "/admin/scheduler-settings#scheduler-link-settings", routeLabel: "Open scheduler settings" },
      { label: "Kickoff call requirements", to: "/admin/scheduler-settings#kickoff-call-requirements", routeLabel: "Open scheduler settings" },
      { label: "Follow-up readiness", to: "/admin/executive-dashboard#follow-up-center", routeLabel: "Open executive dashboard" },
    ],
  },
  "welcome-email-settings": {
    icon: MailCheck,
    title: "Welcome Email Settings",
    eyebrow: "Forms Portal Admin Workflow",
    description: "Configure welcome-email content, delivery timing, and handoff details for new Kreatethedate clients.",
    status: "Welcome email settings page",
    focus: [
      { label: "Welcome email settings", to: "/admin/welcome-email-settings#welcome-email-settings", routeLabel: "Open welcome email settings" },
      { label: "Onboarding next steps", to: "/admin/onboarding-settings#onboarding-next-steps", routeLabel: "Open onboarding settings" },
      { label: "Scheduler-dependent follow-up", to: "/admin/scheduler-settings#scheduler-dependent-follow-up", routeLabel: "Open scheduler settings" },
    ],
  },
  "onboarding-settings": {
    icon: MailCheck,
    title: "Onboarding Settings",
    eyebrow: "Forms Portal Admin Workflow",
    description: "Manage onboarding next steps, client handoff checklists, and the follow-up workflow that begins after welcome delivery.",
    status: "Onboarding settings page",
    focus: [
      { label: "Onboarding next steps", to: "/admin/onboarding-settings#onboarding-next-steps", routeLabel: "Open onboarding settings" },
      { label: "Welcome email settings", to: "/admin/welcome-email-settings#welcome-email-settings", routeLabel: "Open welcome email settings" },
      { label: "Scheduler-dependent follow-up", to: "/admin/scheduler-settings#scheduler-dependent-follow-up", routeLabel: "Open scheduler settings" },
    ],
  },
} satisfies Record<
  AdminWorkflowKey,
  {
    icon: typeof LayoutDashboard;
    title: string;
    eyebrow: string;
    description: string;
    status: string;
    focus: Array<{
      label: string;
      to: string;
      routeLabel: string;
    }>;
  }
>;

const workflowNavigation = [
  { label: "Admin Directory", to: "/admin/forms-directory" },
  { label: "Executive Dashboard", to: "/admin/executive-dashboard" },
  { label: "Truevance Connect Business Center", to: "/admin/truevance-business-center" },
  { label: "Sales Center", to: "/admin/truevance-sales-center" },
  { label: "Assessment Center", to: "/admin/truevance-assessment-center" },
  { label: "Opportunity Management", to: "/admin/truevance-opportunities" },
  { label: "Assessment Records", to: "/admin/truevance-assessments" },
  { label: "Consulting Assessments", to: "/admin/consulting-assessments" },
  { label: "Consulting Recommendations", to: "/admin/truevance-recommendations" },
  { label: "Implementation Roadmaps", to: "/admin/implementation-roadmaps" },
  { label: "Consulting Projects", to: "/admin/truevance-projects" },
  { label: "Proposal Dashboard", to: "/admin/proposal-dashboard" },
  { label: "Create Proposal", to: "/admin/proposals" },
  { label: "Agreement Records", to: "/admin/agreements" },
  { label: "Payment Records", to: "/admin/payments" },
  { label: "Payment Settings", to: "/admin/payment-settings" },
  { label: "Scheduler Settings", to: "/admin/scheduler-settings" },
  { label: "Welcome Email / Onboarding", to: "/admin/welcome-email-settings" },
  { label: "Onboarding Settings", to: "/admin/onboarding-settings" },
  { label: "Client Forms Portal", to: "/" },
];

export function AdminWorkflowPage({ workflow }: { workflow: AdminWorkflowKey }) {
  const details = workflowContent[workflow];
  const Icon = details.icon;
  const location = useLocation();

  useEffect(() => {
    document.title = `${details.title} | Kreatethedate Forms Portal`;
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute("content", `${details.title} private admin workflow page for the Kreatethedate Forms Portal.`);
  }, [details.title]);

  useEffect(() => {
    if (!location.hash) return;
    const target = document.getElementById(location.hash.slice(1));
    target?.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [location.hash]);

  return (
    <main className="command-center-shell min-h-screen text-[#fff9ef]">
      <FormPortalHeader />
      <section className="relative isolate overflow-hidden border-b border-[#D94A16]/18 bg-[#050505] px-4 py-12 text-[#fff9ef] sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,106,43,.18),transparent_34%),linear-gradient(120deg,rgba(5,5,5,.96),rgba(22,18,10,.9))]" />
        <div className="relative mx-auto grid max-w-7xl gap-5">
          <div className="inline-flex w-fit items-center gap-2 rounded-full border border-[#FF6A2B]/35 px-3 py-2 text-xs font-black uppercase tracking-[0.2em] text-[#FF6A2B]">
            <Icon className="size-4" aria-hidden="true" />
            {details.eyebrow}
          </div>
          <h1 className="max-w-4xl font-display text-5xl font-bold leading-tight sm:text-6xl">{details.title}</h1>
          <p className="max-w-3xl text-base font-semibold leading-8 text-[#fff9ef]/76">{details.description}</p>
        </div>
      </section>

      <section className="px-4 py-10 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-5xl gap-6">
          <article className="command-center-card rounded-lg p-5 sm:p-6">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-[#FF6A2B]">Route Status</p>
            <h2 className="mt-2 font-display text-3xl font-bold text-[#fff9ef]">{details.status}</h2>
            <p className="mt-3 text-sm font-semibold leading-7 text-[#fff9ef]/72">
              This page is part of the Forms Portal admin routing layer and is intentionally separate from Memory Media
              Vault public, client, upload, gallery, storage, and vault routes.
            </p>
          </article>

          <article className="command-center-card rounded-lg p-5 sm:p-6">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-[#FF6A2B]">Workflow Focus</p>
            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              {details.focus.map((item) => (
                (() => {
                  const target = new URL(item.to, window.location.origin);
                  const active = location.pathname === target.pathname && (target.hash ? location.hash === target.hash : true);
                  return (
                <Link
                  key={item.label}
                  to={item.to}
                  aria-current={active ? "page" : undefined}
                  className={`focus-ring group grid gap-2 rounded-lg border p-4 text-left text-sm font-bold leading-6 transition hover:-translate-y-0.5 hover:shadow-[0_18px_38px_rgba(5,5,5,.18)] ${
                    active
                      ? "border-[#FF6A2B]/55 bg-[#FF6A2B]/12 text-[#fff9ef] shadow-[0_18px_38px_rgba(5,5,5,.18)]"
                      : "border-[#FF6A2B]/20 bg-white/5 text-[#fff9ef] hover:border-[#FF6A2B]/55 hover:bg-white/10"
                  }`}
                  title={`${item.label}. ${item.routeLabel}`}
                  aria-label={`${item.label}. ${item.routeLabel}`}
                  id={target.hash ? target.hash.slice(1) : undefined}
                >
                  <span className="flex items-start justify-between gap-3">
                    <span>{item.label}</span>
                    <ArrowRight className="size-4 shrink-0 text-[#FF6A2B] transition group-hover:translate-x-1" aria-hidden="true" />
                  </span>
                  <span className="text-xs font-black uppercase tracking-[0.14em] text-[#FF6A2B]">{item.routeLabel}</span>
                </Link>
                  );
                })()
              ))}
            </div>
          </article>

          <article className="command-center-card rounded-lg p-5 sm:p-6">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-[#FF6A2B]">Admin Navigation</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {workflowNavigation.map((item) => {
                const active = location.pathname === item.to;
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={`focus-ring inline-flex min-h-11 items-center justify-center rounded-lg border px-4 text-sm font-extrabold transition ${
                      active
                        ? "border-[#FF6A2B]/55 bg-[#FF6A2B] text-[#050505]"
                        : "border-[#FF6A2B]/24 bg-white/5 text-[#fff9ef] hover:border-[#FF6A2B] hover:bg-white/10"
                    }`}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </div>
          </article>

          <div className="flex flex-col gap-3 sm:flex-row">
            <Link
              to="/admin/forms-directory"
              className="focus-ring inline-flex min-h-12 items-center justify-center rounded-lg bg-[#FF6A2B] px-5 text-sm font-extrabold text-[#050505] transition hover:bg-[#FF6A2B]"
            >
              Back to Admin Forms Directory
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
